import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-haul-polska";
const articleImage = "https://readdy.ai/api/search-image?query=Open%20cardboard%20shipping%20box%20with%20fashion%20items%20spilling%20out%20on%20warm%20cream%20desk%2C%20hoodie%20sneakers%20and%20accessories%20being%20unboxed%2C%20soft%20natural%20morning%20light%2C%20minimalist%20unboxing%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20excitement%20of%20receiving%20package%2C%20clean%20european%20lifestyle%20aesthetic%2C%20editorial%20composition&width=1200&height=800&seq=kakobuy-article-haul&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Haul Polska — Przegląd Zamówień, Unboxing i Pierwsze Wrażenia",
    description: "Zobacz, co polscy kupujący zamawiają na Kakobuy. Przegląd autentycznych zamówień, unboxing i szczere pierwsze wrażenia z produktów.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Haul Polska", path: articlePath },
  ]),
];

export default function KakobuyHaulPolskaArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Haul Polska</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-primary-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shopping-bag-3-line text-primary-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-primary-700 whitespace-nowrap">Haul & Unboxing</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Haul Polska — Przegląd Zamówień, Unboxing i Pierwsze Wrażenia
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Zobacz, co polscy kupujący zamawiają na Kakobuy. Przegląd autentycznych zamówień, unboxing i szczere pierwsze wrażenia z produktów.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 9 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Open%20cardboard%20shipping%20box%20with%20fashion%20items%20spilling%20out%20on%20warm%20cream%20desk%2C%20hoodie%20sneakers%20and%20accessories%20being%20unboxed%2C%20soft%20natural%20morning%20light%2C%20minimalist%20unboxing%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20excitement%20of%20receiving%20package%2C%20clean%20european%20lifestyle%20aesthetic%2C%20editorial%20composition&width=1200&height=800&seq=kakobuy-article-haul&orientation=landscape"
                alt="Kakobuy Haul Polska — Unboxing zamówień"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Czym jest Kakobuy Haul?</h2>
                <p className="mb-3">Termin haul w kontekście zakupów online oznacza zbiorcze zamówienie — najczęściej odzieży i akcesoriów — które jest dokumentowane i pokazywane społeczności. Na YouTube, TikToku i forach można znaleźć setki filmów z unboxingiem zamówień z Kakobuy.</p>
                <p className="mb-3">Dla polskich kupujących haule z Kakobuy są świetnym źródłem informacji — możesz zobaczyć rzeczywisty wygląd produktów, sprawdzić jakość wykonania i przekonać się, czy warto zamówić dany przedmiot.</p>
                <p>W tym artykule prezentujemy przegląd typowego haula z Kakobuy — co zamawiają Polacy, ile to kosztuje i jakie są pierwsze wrażenia po otrzymaniu przesyłki.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Przykładowy Kakobuy Haul — zawartość typowego zamówienia</h2>
                <div className="space-y-3">
                  {[
                    { n: "2 pary sneakersów", p: "ok. 180–300 zł za parę", i: "Białe klasyczne sneakersy i para w stylu retro" },
                    { n: "3 bluzy / hoodie", p: "ok. 80–150 zł za sztukę", i: "Oversize basic, vintage wash i minimalistyczna zip-up" },
                    { n: "5 t-shirtów", p: "ok. 30–60 zł za sztukę", i: "Basicowe koszulki z grubej bawełny w neutralnych kolorach" },
                    { n: "2 pary spodni", p: "ok. 100–180 zł za parę", i: "Cargo pants i jeansy w stylu relaxed fit" },
                    { n: "Akcesoria", p: "ok. 30–80 zł", i: "Czapka, skarpetki, okulary przeciwsłoneczne" },
                  ].map((item, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.n}</h3>
                      <p className="text-xs text-foreground-600">Cena: {item.p} — {item.i}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Koszty — ile kosztuje typowy haul?</h2>
                <p className="mb-3">Koszt typowego haula z Kakobuy zależy od liczby zamówionych produktów i ich kategorii. Przykładowy haul opisany powyżej (ubrania i buty) to wydatek rzędu 800–1300 zł za same produkty. Do tego należy doliczyć koszt wysyłki, który waha się od 100 do 300 zł w zależności od wagi paczki i wybranej linii wysyłkowej.</p>
                <p className="mb-3">Mimo dodatkowych kosztów, łączna cena jest wciąż znacznie niższa niż zakup tych samych produktów w polskich sklepach. To właśnie stosunek jakości do ceny jest głównym powodem popularności Kakobuy Haul w Polsce.</p>
                <p>Dodatkowo, dzięki konsolidacji zamówień (łączeniu produktów od różnych sprzedawców w jedną paczkę), koszt wysyłki na sztukę znacząco spada.</p>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Stwórz swój własny haul</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Znajdź najlepsze produkty do swojego haula w Kakobuy Spreadsheet i podziel się wrażeniami ze społecznością.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}